import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { X, Music } from 'lucide-react';

const ArtistBetaBanner = () => {
  // Calculate initial visibility based on localStorage
  const initialVisibility = useMemo(() => {
    const dismissedAt = localStorage.getItem('artistBetaBannerDismissed');
    const now = new Date().getTime();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return !dismissedAt || (now - parseInt(dismissedAt)) > twentyFourHours;
  }, []);

  const [isVisible, setIsVisible] = useState(initialVisibility);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    // Trigger fade-in animation after component mounts
    if (isVisible) {
      const timer = setTimeout(() => setFadeIn(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleDismiss = () => {
    setFadeIn(false);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem('artistBetaBannerDismissed', new Date().getTime().toString());
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`relative transition-all duration-800 ease-in-out ${
        fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      }`}
      style={{ transitionDuration: '0.8s' }}
    >
      <div className="bg-black border border-gray-800 rounded-xl shadow-xl overflow-hidden">
        <div className="relative p-6 md:p-8">
          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
            aria-label="Dismiss banner"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col md:flex-row items-center gap-6 max-w-5xl mx-auto">
            {/* Icon */}
            <div className="flex-shrink-0 w-16 h-16 bg-[#F2D857]/10 rounded-full flex items-center justify-center">
              <Music className="w-8 h-8 text-[#F2D857]" />
            </div>

            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                <span className="text-[#F2D857]">🎤 Artist Access</span> Is Currently in Private Beta
              </h3>
              <p className="text-gray-300">
                Want feedback and exposure without getting buried? Join early and lock your spot before we open publicly.
              </p>
            </div>

            {/* CTA Button */}
            <Link
              to="/artist-coming-soon"
              className="flex-shrink-0 px-8 py-3 bg-[#F2D857] hover:bg-black hover:text-[#F2D857] text-black font-bold rounded-lg transition-all duration-300 border-2 border-[#F2D857] transform hover:scale-105"
            >
              Join the Waitlist
            </Link>
          </div>

          {/* Bottom gradient accent */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#F2D857] via-[#F2D857]/50 to-[#F2D857]"></div>
        </div>
      </div>
    </div>
  );
};

export default ArtistBetaBanner;
